import {Chip, createStyles, Group, NumberInput, Select, Stack, Text} from '@mantine/core';
import React from 'react';

import {TimeTriggerConfig} from './RoutineTypes';

interface TimeTriggerConfigFormProps {
  config: Partial<TimeTriggerConfig>;
  onChange: (updates: Partial<TimeTriggerConfig>) => void;
}

const FREQ_OPTIONS = [
  {value: 'daily', label: 'Day'},
  {value: 'weekly', label: 'Week'},
  {value: 'monthly', label: 'Month'},
];

const DAY_OPTIONS = [
  {value: '1', label: 'Mondays'},
  {value: '2', label: 'Tuesdays'},
  {value: '3', label: 'Wednesdays'},
  {value: '4', label: 'Thursdays'},
  {value: '5', label: 'Fridays'},
  {value: '6', label: 'Saturdays'},
  {value: '0', label: 'Sundays'},
];

const TIME_OPTIONS = [
  {value: '08:00', label: 'Morning'},
  {value: '12:00', label: 'Afternoon'},
  {value: '18:00', label: 'Evening'},
];

const useStyles = createStyles((theme) => ({
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    flexWrap: 'wrap' as const,
  },
  fieldLabel: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 500,
    flexShrink: 0,
  },
  chipGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  pillChip: {
    '& .mantine-Chip-label': {
      borderRadius: 16,
      padding: '4px 12px',
      fontSize: theme.fontSizes.xs,
      fontWeight: 500,
      cursor: 'pointer',
    },
    '& .mantine-Chip-iconWrapper': {
      display: 'none',
    },
  },
}));

export default function TimeTriggerConfigForm({config, onChange}: TimeTriggerConfigFormProps): JSX.Element {
  const {classes} = useStyles();

  const weeklyDaysAsStrings = (config.weekly_days ?? []).map((d) => String(d));

  return (
    <Stack spacing="md">
      <div className={classes.fieldRow}>
        <Text className={classes.fieldLabel}>Repeat every</Text>
        <NumberInput
          value={config.recurrence_interval ?? 1}
          onChange={(val) => {
            onChange({recurrence_interval: typeof val === 'number' ? val : 1});
          }}
          min={1}
          max={52}
          size="sm"
          sx={{width: 64}}
        />
        <Select
          value={config.freq_type ?? 'weekly'}
          onChange={(val) => {
            if (val) {
              onChange({
                freq_type: val as TimeTriggerConfig['freq_type'],
                weekly_days: val === 'weekly' ? (config.weekly_days ?? []) : [],
              });
            }
          }}
          data={FREQ_OPTIONS}
          size="sm"
          sx={{width: 110}}
          // TODO(nweeden): Dropdown has extra gap due to global Popover withArrow default
        />
      </div>

      {(config.freq_type ?? 'weekly') === 'weekly' && (
        <Group spacing="sm" align="flex-start">
          <Text className={classes.fieldLabel}>On</Text>
          <Chip.Group
            multiple={true}
            value={weeklyDaysAsStrings}
            onChange={(vals: Array<string>) => {
              onChange({weekly_days: vals.map((v) => Number(v))});
            }}
          >
            <div className={classes.chipGroup}>
              {DAY_OPTIONS.map((day) => (
                <Chip
                  key={day.value}
                  value={day.value}
                  size="xs"
                  variant="filled"
                  className={classes.pillChip}
                >
                  {day.label}
                </Chip>
              ))}
            </div>
          </Chip.Group>
        </Group>
      )}

      <Group spacing="sm" align="flex-start">
        <Text className={classes.fieldLabel}>During</Text>
        <Chip.Group
          multiple={false}
          value={config.run_time ?? ''}
          onChange={(val: string) => {
            onChange({run_time: val});
          }}
        >
          <div className={classes.chipGroup}>
            {TIME_OPTIONS.map((time) => (
              <Chip
                key={time.value}
                value={time.value}
                size="xs"
                variant="filled"
                className={classes.pillChip}
              >
                {time.label}
              </Chip>
            ))}
          </div>
        </Chip.Group>
      </Group>
    </Stack>
  );
}
